import React, { useEffect, useState, useMemo } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import styles from "./EligibilityTrackerHistoricalPatientPage.scss";
import ClinicBase from "components/layout/ClinicBase";
import { getClinicInfo } from "utils/ClinicData";
import { getHistoricalPatientHistory } from "actions/ClinicActions";
import SortableDataTable from "components/common/SortableDataTable";
import OpenInvoicePDFLinkNewTab from "components/common/OpenInvoicePDFLinkNewTab";
import * as ClinicApi from "api/ClinicApi";
import Button from "components/common/Button";
import toast from "utils/toast";
import TextBox from "components/common/TextBox";
import { downloadFile } from "utils/request";
import moment from "moment";
import { debounce } from "lodash";

function sortByDate(arr, order = "desc") {
  if (!Array.isArray(arr)) return [];
  return [...arr].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    if (order === "asc") {
      return dateA - dateB;
    }
    return dateB - dateA;
  });
}

function EligibilityTrackerHistoricalPatientPage(props) {
  const { clientId, clinicId, patientId, loadPatientHistory, patientHistoryData } = props;
  const { clientName, patientName, hasLoggedIn, invoices, clientRemoteDisplayId } = patientHistoryData || {};
  const [searchQuery, setSearchQuery] = useState("");

  const history = useHistory();

    // Debounced function to update debouncedValue
  const debouncedSetDebouncedValue = useMemo(
    () => debounce((value) => {
      loadPatientHistory(clinicId, clientId, patientId, value);
    }, 500), // Adjust debounce delay as needed (e.g., 500ms)
    []
  );

  useEffect(() => {
    if (clinicId && clientId && patientId) {
      loadPatientHistory(clinicId, clientId, patientId);
    }
  }, [clinicId, clientId, loadPatientHistory]);

    useEffect(() => {
    // Call the debounced function whenever searchQuery changes
    debouncedSetDebouncedValue(searchQuery);

    // Cleanup function to cancel any pending debounced calls
    return () => {
      debouncedSetDebouncedValue.cancel();
    };
  }, [searchQuery, debouncedSetDebouncedValue]);

  const COLUMNS = [{
    name: "Invoice",
    selector: "invoiceNumber",
    key: "invoiceNumber",
    format: row => (
      <OpenInvoicePDFLinkNewTab
          className="text-primary"
          invoiceId={row.invoiceId}
          extendedInvoiceNumber={row.invoiceNumber}
      /> 
        )
    }, {
      name: "Date",
      selector: "date",
      key: "date",
      sortable: true,
      format: row => (
          <div>
              {moment(row.date).format("MM/DD/YYYY")}
          </div>
      )
    }, {
      name: "Previous Invoice",
      selector: "amount",
      key: "previousInvoiceNumber",
      format: row => (
          <div>
              {row.previousInvoiceNumber || "None"}
          </div>
      )
    },{
      name: "Offers Generated",
      selector: "offersGenerated",
      key: "offersGenerated",
      format: row => (
          <div>
               {Array.isArray(row?.offersGenerated) && row.offersGenerated.length > 0 ? (
              row.offersGenerated.map(offer => offer.offerName).join(", ")
          ) : "None"}
          </div>
      )
    }, {
      name: "Available Offers At Invoice",
      selector: "availableOffersAtInvoiceTime",
      key: "availableOffersAtInvoiceTime",
      format: row => (
        <div>
          {Array.isArray(row?.availableOffersAtInvoiceTime) && row.availableOffersAtInvoiceTime.length > 0 ? (
              row.availableOffersAtInvoiceTime.map(offer => offer.offerName).join(", ")
          ) : "None"}
        </div>
      )
    }
  ];

  const handleDownloadExcel = async () => {
      toast.success("Generating Export...");
      const res = await ClinicApi.getHistoricalPatientHistoryExport(clinicId, clientId, patientId);

      downloadFile(res, "xlsx");
  }

  return (
    <ClinicBase
      clinicId={clinicId}
      docTitle="Eligibility Tracker Client History"
      pageTitle="Eligibility Tracker - Client History"
    >
      <div className="flex flex-column full-width">
        <Button
          className={styles.backButton}
          icon
          onClick={() =>
            history.push(
              `/admin/clinic/${clinicId}/programs/eligibility-tracker/history/${clientId}`
            )
          }
        >
          <i className="fa fa-arrow-left fa-fw" /> Pet Selection
        </Button>
        <header className={styles.header}>
          <h1>Eligibility History</h1>
        </header>
        <section className={styles.root}>
          <h2 className={styles.subheader}>Patient Summary</h2>
          <div className={styles.grid}>
            <dl className={styles.gridList}>
              <div className={styles.gridRow}>
                <dt className={styles.gridHeading}>Pet Name</dt>
                {patientId && <dd className={styles.gridValue}>{patientName}</dd>}
              </div>
              <div className={styles.gridRow}>
                <dt className={styles.gridHeading}>Client Name</dt>
                {clientName && <dd className={styles.gridValue}>{clientName || "N/A"}</dd>}
              </div>
              <div className={styles.gridRow}>
                <dt className={styles.gridHeading}>Client ID</dt>
                {clientRemoteDisplayId && <dd className={styles.gridValue}>{clientRemoteDisplayId || "N/A"}</dd>}
              </div>
              <div className={styles.gridRow}>
                <dt className={styles.gridHeading}>Clinic ID</dt>
                {clinicId && <dd className={styles.gridValue}>{clinicId || "N/A"}</dd>}
              </div>
              <div className={styles.gridRow}>
                <dt className={styles.gridHeading}>QR Code Scanned</dt>
                <dd className={styles.gridValue}>
                  {hasLoggedIn ? (
                    <i className={`fa-regular fa-circle-check ${styles.greenIcon}`}></i>
                  ) : (
                    <i className={`fa-regular fa-circle-xmark ${styles.redIcon}`}></i>
                  )}
                </dd>
              </div>
            </dl>
          </div>
        </section>
        <div className={styles.invoiceHistory}> 
          <div className={styles.invoiceHistoryHeading}>
            <h2>Invoice History</h2>
            <Button icon onClick={handleDownloadExcel}><i className="fa fa-download"></i> Export</Button>
          </div>
          <div className="margin-bottom-lg">
            <TextBox
              onChange={({value}) => setSearchQuery(value)}
              name="search"
              value={searchQuery}
              placeholder="Search for invoices or offers"
              noPadding
          />
          </div>
          <SortableDataTable
            columns={COLUMNS}
            rawData={invoices && invoices.length > 0 ? sortByDate(invoices) : []}
            striped
            green
            resultsPerPage={6}
            pagination
          />
        </div>
      </div>
    </ClinicBase>
  );
}

export default connect((state, ownProps) => {
  const adminClinicId = Number(ownProps.match.params.clinicId);
  const clinic = getClinicInfo(state, adminClinicId);
  const clinicId = clinic ? clinic.clinicId : undefined;
  const patientHistoryData = state.entities.historicalPatientsHistory.data;

  return {
    clinicId,
    clientId: ownProps.match.params.clientId || null,
    patientId: ownProps.match.params.patientId || null,
    patientHistoryData,
  };
}, (dispatch) => ({
        loadPatientHistory: (clinicId, clientId, patientId, query) => dispatch(getHistoricalPatientHistory(clinicId, clientId, patientId, query)),
  })
)(EligibilityTrackerHistoricalPatientPage);
